import React, { useState, useRef, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Stars } from "@react-three/drei";
import "./MusicGame.css";

// === 3D: Notas flotantes decorativas ===
function FloatingNote({ position, color = "#ffd93d" }) {
  const ref = useRef();
  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.y += 0.01;
      ref.current.position.y =
        position[1] + Math.sin(state.clock.elapsedTime + position[0]) * 0.15;
    }
  });
  return (
    <mesh ref={ref} position={position}>
      <boxGeometry args={[0.25, 0.1, 0.05]} />
      <meshStandardMaterial color={color} metalness={0.5} roughness={0.2} />
    </mesh>
  );
}

export default function MusicGame() {
  const [name, setName] = useState(localStorage.getItem("mg_name") || "");
  const [started, setStarted] = useState(false);
  const [levelIndex, setLevelIndex] = useState(0);
  const [qIndex, setQIndex] = useState(0);
  const [feedback, setFeedback] = useState(null);

  const mainVideoRef = useRef(null);
  const bgAudioRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(true);
  const [videoMuted, setVideoMuted] = useState(false);
  const [videoVolume, setVideoVolume] = useState(0.4);
  const [videoIndex, setVideoIndex] = useState(0);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [musicMuted, setMusicMuted] = useState(false);
  useEffect(() => {
  const video = mainVideoRef.current;
  if (video) {
    video.muted = true; // asegura autoplay inicial
    video.play().catch(() => {});
  }
}, []);



  // === Lista de videos ===
  const videos = [
    "/assets/videos/intro.mp4",
    "/assets/videos/level1.mp4",
    "/assets/videos/level2.mp4",
    "/assets/videos/stars.mp4",
    "/assets/videos/galaxy.mp4",
    "/assets/videos/intro.mp4",
    "/assets/videos/level1.mp4",
    "/assets/videos/level2.mp4",
    "/assets/videos/stars.mp4",
    "/assets/videos/galaxy.mp4",


  ];

  // === Control de videos secuenciales ===
 useEffect(() => {
  const video = mainVideoRef.current;
  if (!video) return;

  const handleLoaded = () => {
    setVideoLoaded(true);
    video.play().catch(() => {});
  };

  const handleEnd = () => {
    const next = (videoIndex + 1) % videos.length;
    setVideoIndex(next);
  };

  video.addEventListener("loadeddata", handleLoaded);
  video.addEventListener("ended", handleEnd);

  // Reinicia el video desde el principio y establece la nueva fuente
  video.src = videos[videoIndex];
  video.currentTime = 0; // Reinicia el tiempo del video
  video.volume = videoVolume;

  return () => {
    video.removeEventListener("loadeddata", handleLoaded);
    video.removeEventListener("ended", handleEnd);
  };
}, [videoIndex, videoVolume]);




  const toggleVideoPlay = () => {
    const video = mainVideoRef.current;
    if (!video) return;
    if (isPlaying) video.pause();
    else video.play().catch(() => {});
    setIsPlaying(!isPlaying);
  };

  const toggleVideoMute = () => setVideoMuted((m) => !m);





  // === Música de fondo ===
  useEffect(() => {
    const bg = bgAudioRef.current;
    if (!bg) return;
    bg.volume = 0.5;
    if (musicMuted) bg.pause();
    else bg.play().catch(() => {});
  }, [musicMuted, started]);

  // === Preguntas ===
  const LEVELS = [
    {
      id: "notes",
      title: "Teoría Musical",
      questions: [
        {
          q: "¿Qué nota está en la primera línea del pentagrama (clave de Sol)?",
          img: "/assets/images/quarter-eighths.png",
          options: ["Sol", "Fa", "Mi", "Re"],
          answer: 2,
        },




   {
          q: '¿Qué nota está en la segunda línea del pentagrama (clave de Sol)?',
          img: '/assets/images/quarter-eighths.png',
          options: ['Sol', 'Fa', 'Mi', 'Re'],
          answer: 0,
        },
        {
          q: '¿Cuál es esta figura musical?',
          img: '/assets/images/quarter-note.png',
          options: ['Negra', 'Corchea', 'Blanca', 'Semicorchea'],
          answer: 0,
        },


	{
          q: '¿Qué es un pentagrama Musical?',
          img: '/assets/images/pentagrama3.png',
          options: ['Una figura geométrica', 'Un instrumento musical', 'Un tipo de clave', 'Un conjunto de cinco líneas y cuatro espacios donde se escriben las notas musicales'],
          answer: 3,
        },


	{
          q: '¿Qué es una nota musical?',
          img: '/assets/images/quarter-eighths.png',
          options: ['Un símbolo que representa una duración de sonido', 'Un tipo de acorde', 'Una forma de escribir música', 'Un tipo de instrumento'],
          answer: 0,
        },

{
          q: '¿Qué clave se utiliza comúnmente para las notas más graves?',
          img: '/assets/images/clave_fa1.png',
          options: ['Clave de sol', 'Clave de do', 'Clave de fa', 'Clave de re'],
          answer: 2,
        },

	{
          q: '¿Cuántos tiempos hay en un compás de 4/4?',
          img: '/assets/images/clave_fa1.png',
          options: ['2', '3', '4', '6'],
          answer: 2,
        },


		{
          q: '¿Cuál es esta figura musical?',
          img: '/assets/images/quarter-note.png',
          options: ['Negra', 'Corchea', 'Blanca', 'Semicorchea'],
          answer: 0,
        },
      ],
    },
    {
      id: 'rhythm',
      title: 'Compases y Ritmo',
      questions: [
        {
          q: '¿Cuántos pulsos tiene un compás 3/4?',
          img: '/assets/images/compas3_4.png',
          options: ['2', '3', '4', '6'],
          answer: 1,
        },
        {
          q: '¿Cuántas corcheas hay en una negra?',
          img: '/assets/images/figuras1.png',
          options: ['1', '2', '3', '4'],
          answer: 1,
        },
      ],
    },
    {
      id: 'staff',
      title: 'Pentagrama y Claves',
      questions: [
        {
          q: '¿Cuántas líneas tiene el pentagrama?',
          img: '/assets/images/quarter-eighths.png',
          options: ['4', '5', '6', '3'],
          answer: 1,
        },

{
          q: '¿Qué símbolo se utiliza para indicar un silencio?',
          img: '/assets/images/figuras1.png',
          options: ['Nota', 'Ligadura', 'Silencio', 'Barrido'],
          answer: 2,
        },

{
          q: '¿Qué es un compás?',
          img: '/assets/images/clave_fa1.png',
          options: ['Un instrumento de medición', 'Una división del tiempo en la música', 'Una técnica de interpretación', 'Un tipo de melodía'],
          answer: 1,
        },



{
          q: '¿Qué es un ritmo?',
          img: '/assets/images/staff-lines.png',
          options: ['La velocidad de la música', 'La combinación de sonidos y silencios en el tiempo', 'La altura de las notas', 'La forma de un pentagrama'],
          answer: 1,
        },


{
          q: '¿Qué se indica en un compás 3/4?',
          img: '/assets/images/compas3_4.png',
          options: ['Tres tiempos por compás y la negra recibe un tiempo', 'Cuatro tiempos por compás y la blanca recibe un tiempo', 'Dos tiempos por compás y la corchea recibe un tiempo', 'Tres tiempos por compás y la blanca recibe un tiempo'],
          answer: 0,
        },



        {
          q: '¿Qué es una semicorchea?',
          img: '/assets/images/semicorchea.png',
          options: [
            'Una nota que dura 1/2 tiempo',
            'Una nota que dura un 1/4 de tiempo ',
            'Una nota que dura un 1 tiempo completo',
            'Una nota que dura un 1/8 de tiempo',
          ],
          answer: 1,
        },







        {
          q: "¿Cuál es esta figura musical?",
          img: "/assets/images/quarter-note.png",
          options: ["Negra", "Corchea", "Blanca", "Semicorchea"],
          answer: 0,
        },
        {
          q: "¿Qué es un pentagrama Musical?",
          img: "/assets/images/pentagrama3.png",
          options: [
            "Una figura geométrica",
            "Un instrumento musical",
            "Un tipo de clave",
            "Un conjunto de cinco líneas y cuatro espacios donde se escriben las notas musicales",
          ],
          answer: 3,
        },
      ],
    },
  ];

  const currentLevel = LEVELS[levelIndex];
  const question = currentLevel?.questions[qIndex];

  const start = () => {
    if (!name.trim()) return;
    setStarted(true);
    setQIndex(0);
    const bg = bgAudioRef.current;
    if (bg) {
      bg.currentTime = 0;
      bg.play().catch(() => {});
    }
  };

  const answer = (i) => {
    const correct = i === question.answer;
    setFeedback(correct ? "correct" : "wrong");

    setTimeout(() => {
      setFeedback(null);
      const nextQ = qIndex + 1;
      if (nextQ < currentLevel.questions.length) {
        setQIndex(nextQ);
      } else {
        const nextLevel = levelIndex + 1;
        if (nextLevel < LEVELS.length) {
          setLevelIndex(nextLevel);
          setQIndex(0);
        } else {
          alert("🎵 ¡Excelente! Has completado todas las preguntas 🎶");
          setStarted(false);
          if (bgAudioRef.current) bgAudioRef.current.pause();
        }
      }
    }, 1000);
  };

  if (!started) {
    return (
      <div className="mg-root">
        <div className="mg-welcome card">
          <h2>🎶 Nelson Music Lab</h2>
          <p>Aprende teoría musical jugando</p>
          <div className="form-row">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre"
            />
            <button className="btn" onClick={start} disabled={!name.trim()}>
              Iniciar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mg-root">
      {/* 🎵 Música de fondo */}
      <audio ref={bgAudioRef} src="/assets/sounds/bg-loop.mp3" loop />

      <div className="mg-game">
        {/* Panel izquierdo */}
        <div className="mg-left card">
          <h3>{currentLevel.title}</h3>
          <p>{question?.q}</p>

          {question && (
            <div className="img-wrap">
              <img src={question.img} alt="question" />
            </div>
          )}

          <div className="options-grid">
            {question?.options.map((opt, i) => (
              <button key={i} className="opt-btn" onClick={() => answer(i)}>
                {opt}
              </button>
            ))}
          </div>

          {feedback && (
            <div
              className={`feedback ${
                feedback === "correct" ? "correct" : "wrong"
              }`}
            >
              {feedback === "correct" ? "✅ Correcto" : "❌ Incorrecto"}
            </div>
          )}
        </div>

        {/* Panel derecho */}
        <div className="mg-right card">
          <div className="mini-canvas">
            <Canvas camera={{ position: [0, 0.8, 2.5], fov: 55 }}>
              <ambientLight intensity={0.7} />
              <directionalLight position={[2, 2, 3]} intensity={0.6} />
              <Stars radius={30} depth={40} count={800} factor={3} fade />
              <FloatingNote position={[-0.5, 0.2, 0]} color="#61dafb" />
              <FloatingNote position={[0.3, 0.3, 0]} color="#ffd93d" />
              <FloatingNote position={[0.8, 0.1, 0]} color="#ff6b6b" />
              <OrbitControls enablePan={false} enableZoom={false} />
            </Canvas>
          </div>

          <div className="video-box">
            {!videoLoaded && (
              <div className="video-loading">Cargando video...</div>
            )}
            <video
              ref={mainVideoRef}
              className="side-video"
              autoPlay
              muted={videoMuted}
              playsInline
	     style={{
                opacity: videoLoaded ? 1 : 0,
                transition: "opacity 0.5s ease-in-out",
              }}
            />

            <div className="video-controls">
              <button className="btn ghost" onClick={toggleVideoPlay}>
                {isPlaying ? "⏸️" : "▶️"}
              </button>
              <button className="btn ghost" onClick={toggleVideoMute}>
                {videoMuted ? "🔇" : "🔊"}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={videoVolume}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setVideoVolume(v);
                  if (mainVideoRef.current)
                    mainVideoRef.current.volume = v;
                }}
              />
              <button
                className="btn ghost"
                onClick={() => setMusicMuted((m) => !m)}
              >
                {musicMuted ? "🎵 Activar Música" : "🔇 Silenciar Música"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
